import React from "react";
import {
    Box, Grid, TextField, Typography, Button, Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HomologationPicker from "./HomologationPicker";

// Fiche de course commune aux 3 types (repris de TimingSwissSkiExport) :
// en-tête d'impression, jury, données techniques, infos par manche.
// Pour une course populaire, le jury FIS et l'homologation sont masqués.

export const EMPTY_RUN = () => ({
    courseSetter: "", courseSetterNation: "", gates: "", turningGates: "",
    startTime: "", endTime: "",
    forerunners: [{ name: "", nation: "" }, { name: "", nation: "" }, { name: "", nation: "" }],
    weather: "", snow: "", tempStart: "", tempFinish: "",
});

export const EMPTY_SHEET = {
    bannerImage: "", logoLeft: "", logoRight: "", raceTitle: "", subTitle: "", footerText: "", footerLogo: "",
    jury: { td: "", tdNation: "", referee: "", refereeNation: "", assistant: "", assistantNation: "", chief: "", chiefNation: "" },
    technical: { courseName: "", startAltitude: "", finishAltitude: "", verticalDrop: "", length: "", homologation: "" },
    runs: [EMPTY_RUN(), EMPTY_RUN()],
};

export function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

function LogoField({ label, value, onChange }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
            {value && (
                <Box component="img" src={value} alt={label}
                     sx={{ maxHeight: 80, maxWidth: 200, display: "block", mb: 1, border: "1px solid #eee" }} />
            )}
            <input type="file" accept="image/png,image/jpeg"
                   onChange={async (e) => { const f = e.target.files[0]; if (f) onChange(await fileToDataUrl(f)); }} />
            {value && <Button size="small" color="error" onClick={() => onChange("")} sx={{ ml: 1 }}>Retirer</Button>}
        </Box>
    );
}

function Section({ title, defaultExpanded, children }) {
    return (
        <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">{title}</Typography>
            </AccordionSummary>
            <AccordionDetails>{children}</AccordionDetails>
        </Accordion>
    );
}

export default function RaceSheetForm({ config, setConfig }) {
    const isPop = config.raceType === "populaire";
    const set = (field, value) => setConfig((p) => ({ ...p, [field]: value }));
    const setJury = (field, value) => setConfig((p) => ({ ...p, jury: { ...p.jury, [field]: value } }));
    const setTech = (field, value) => setConfig((p) => ({ ...p, technical: { ...p.technical, [field]: value } }));
    // Piste homologuée choisie dans la liste FIS : pré-remplit les données
    // techniques, qui restent modifiables ensuite (01.10.2026).
    const applyHomologation = (c) =>
        setConfig((p) => ({
            ...p,
            technical: {
                ...p.technical,
                courseName: c.course,
                homologation: c.homologation,
                startAltitude: c.start ?? "",
                finishAltitude: c.finish ?? "",
                verticalDrop: c.vd ?? (c.start != null && c.finish != null ? c.start - c.finish : ""),
                length: c.length ?? "",
            },
        }));
    const setRun = (i, field, value) =>
        setConfig((p) => {
            const runs = [...p.runs];
            runs[i] = { ...runs[i], [field]: value };
            return { ...p, runs };
        });
    const setFr = (i, fi, field, value) =>
        setConfig((p) => {
            const runs = [...p.runs];
            const forerunners = [...runs[i].forerunners];
            forerunners[fi] = { ...forerunners[fi], [field]: value };
            runs[i] = { ...runs[i], forerunners };
            return { ...p, runs };
        });

    const juryRows = isPop
        ? [{ key: "chief", nationKey: "chiefNation", label: "Chef de course" }]
        : [
            { key: "td", nationKey: "tdNation", label: config.raceType === "fis" ? "FIS Technical Delegate" : "Technical Delegate" },
            { key: "referee", nationKey: "refereeNation", label: "Referee" },
            { key: "assistant", nationKey: "assistantNation", label: "Assistant Referee" },
            { key: "chief", nationKey: "chiefNation", label: "Chief of Race" },
        ];

    const tf = (label, value, onChange, xs = 6) => (
        <Grid item xs={12} sm={xs}>
            <TextField label={label} fullWidth size="small" value={value || ""} onChange={(e) => onChange(e.target.value)} />
        </Grid>
    );

    return (
        <>
            <Section title="En-tête et pied de page des PDF">
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}><LogoField label="Logo gauche" value={config.logoLeft} onChange={(v) => set("logoLeft", v)} /></Grid>
                    <Grid item xs={12} md={6}><LogoField label="Logo droit" value={config.logoRight} onChange={(v) => set("logoRight", v)} /></Grid>
                    {tf("Titre (par défaut : Event)", config.raceTitle, (v) => set("raceTitle", v), 12)}
                    {tf("Sous-titre", config.subTitle, (v) => set("subTitle", v), 12)}
                    {tf("Texte de pied de page", config.footerText, (v) => set("footerText", v), 12)}
                    {/* Bandeau en bas de chaque page (sponsors), 2 à 4 cm de haut — 04.11.2026. */}
                    <Grid item xs={12} md={8}>
                        <LogoField label="Bandeau en bas de page (sponsors)" value={config.bannerImage} onChange={(v) => set("bannerImage", v)} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            label="Hauteur du bandeau"
                            fullWidth
                            size="small"
                            value={config.bannerHeightCm || 3}
                            onChange={(e) => set("bannerHeightCm", Number(e.target.value))}
                            SelectProps={{ native: true }}
                        >
                            {[2, 2.5, 3, 3.5, 4].map((v) => (
                                <option key={v} value={v}>{`${v} cm`}</option>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                            Utilisé par toutes les listes (départ, résultats, analyse, rapport de l'arbitre). Le logo
                            321-timing est ajouté automatiquement en pied de page, entre « Timing » et « Data processing »,
                            avec la date et l'heure de création du document.
                        </Typography>
                    </Grid>
                </Grid>
            </Section>

            <Section title={isPop ? "Organisation et parcours" : "Jury et données techniques"}>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {juryRows.map((row) => (
                        <React.Fragment key={row.key}>
                            {tf(row.label, config.jury?.[row.key], (v) => setJury(row.key, v), 9)}
                            {tf("Nation", config.jury?.[row.nationKey], (v) => setJury(row.nationKey, v.toUpperCase()), 3)}
                        </React.Fragment>
                    ))}
                </Grid>
                <HomologationPicker
                    discipline={config.discipline}
                    raceDate={config.date}
                    value={config.technical?.homologation}
                    onSelect={applyHomologation}
                />
                <Grid container spacing={2}>
                    {tf("Nom de la piste", config.technical?.courseName, (v) => setTech("courseName", v))}
                    {tf("N° d'homologation", config.technical?.homologation, (v) => setTech("homologation", v))}
                    {tf("Altitude départ (m)", config.technical?.startAltitude, (v) => setTech("startAltitude", v), 3)}
                    {tf("Altitude arrivée (m)", config.technical?.finishAltitude, (v) => setTech("finishAltitude", v), 3)}
                    {tf("Dénivelé (m)", config.technical?.verticalDrop, (v) => setTech("verticalDrop", v), 3)}
                    {tf("Longueur (m)", config.technical?.length, (v) => setTech("length", v), 3)}
                </Grid>
            </Section>

            {(config.runs || []).map((run, i) => (
                <Section key={i} title={`Manche ${i + 1}`}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        {tf("Traceur", run.courseSetter, (v) => setRun(i, "courseSetter", v), 6)}
                        {tf("Nation", run.courseSetterNation, (v) => setRun(i, "courseSetterNation", v.toUpperCase()), 2)}
                        {tf("Heure départ", run.startTime, (v) => setRun(i, "startTime", v), 2)}
                        {tf("Heure fin", run.endTime, (v) => setRun(i, "endTime", v), 2)}
                        {tf("Portes", run.gates, (v) => setRun(i, "gates", v), 3)}
                        {tf("Changements de direction", run.turningGates, (v) => setRun(i, "turningGates", v), 3)}
                    </Grid>
                    {!isPop && (
                        <>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Ouvreurs</Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                {run.forerunners.map((fr, fi) => (
                                    <React.Fragment key={fi}>
                                        {tf(`Ouvreur ${fi + 1}`, fr.name, (v) => setFr(i, fi, "name", v), 3)}
                                        {tf("Nation", fr.nation, (v) => setFr(i, fi, "nation", v.toUpperCase()), 1)}
                                    </React.Fragment>
                                ))}
                            </Grid>
                        </>
                    )}
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Conditions</Typography>
                    <Grid container spacing={2}>
                        {tf("Météo", run.weather, (v) => setRun(i, "weather", v), 3)}
                        {tf("Neige", run.snow, (v) => setRun(i, "snow", v), 3)}
                        {tf("Température départ", run.tempStart, (v) => setRun(i, "tempStart", v), 3)}
                        {tf("Température arrivée", run.tempFinish, (v) => setRun(i, "tempFinish", v), 3)}
                    </Grid>
                </Section>
            ))}
        </>
    );
}